import React, { useEffect } from "react";
import { X } from "lucide-react";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  className?: string;
  hideChrome?: boolean; // removes header, background and default paddings
  keepMounted?: boolean; // when true, keeps content mounted and toggles visibility
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  className = "",
  hideChrome = false,
  keepMounted = false,
}) => {
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  if (!keepMounted && !isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center ${isOpen ? "" : "opacity-0 pointer-events-none"}`}
      data-oid="w1997ks"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        data-oid="5lz00ud"
      />

      {/* Modal Content */}
      <div
        className={`relative ${hideChrome ? "bg-transparent shadow-none max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden" : "bg-elegant-white rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto"} ${className}`}
        data-oid="xrbkgj."
      >
        {/* Header */}
        {!hideChrome && title && (
          <div
            className="sticky top-0 bg-elegant-white border-b border-gray-200 px-6 py-4 rounded-t-xl"
            data-oid="8optlay"
          >
            <div
              className="flex items-center justify-between"
              data-oid="5-ws-4b"
            >
              <h2
                className="text-2xl md:text-3xl font-playfair font-bold text-pine-green"
                data-oid="iogs_zy"
              >
                {title}
              </h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Close modal"
                data-oid="x5aql:0"
              >
                <X className="w-6 h-6 text-gray-600" data-oid="at1np_6" />
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className={hideChrome ? "" : "p-6"} data-oid="tlo8:md">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal;
